import React, { useRef, useState } from 'react';
import { Camera, Trash2, User as UserIcon } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/Button';
import { useNotification } from '../../../contexts/NotificationContext';
import {
  deleteUserPhoto,
  uploadUserPhoto,
  useUserPhotoBlobUrl,
  type User,
} from '../useData';

function userInitials(user: Pick<User, 'userFirstName' | 'userLastName' | 'userEmail'>): string {
  const fromName = `${user.userFirstName?.[0] ?? ''}${user.userLastName?.[0] ?? ''}`.trim();
  if (fromName) return fromName.toUpperCase();
  return (user.userEmail?.[0] ?? '?').toUpperCase();
}

export interface UserPhotoSectionProps {
  userId: number;
  user: Pick<User, 'userFirstName' | 'userLastName' | 'userEmail' | 'userPhotoPath'> & {
    userDisplayName?: string | null;
  };
  onPhotoChange?: () => void;
}

const UserPhotoSection: React.FC<UserPhotoSectionProps> = ({ userId, user, onPhotoChange }) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const { photoBlobUrl, loading } = useUserPhotoBlobUrl(userId, user.userPhotoPath);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setUploading(true);
      await uploadUserPhoto(userId, file);
      showSuccess('Profile photo updated');
      onPhotoChange?.();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    try {
      setRemoving(true);
      await deleteUserPhoto(userId);
      showSuccess('Profile photo removed');
      onPhotoChange?.();
    } catch {
      showError('Failed to remove photo');
    } finally {
      setRemoving(false);
    }
  };

  const displayName =
    user.userDisplayName || `${user.userFirstName || ''} ${user.userLastName || ''}`.trim() || user.userEmail;

  return (
    <div className={cn('rounded-2xl border p-4 sm:p-5', colors.border, colors.surface)}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="relative shrink-0 self-center sm:self-start">
          <div
            className={cn(
              'w-24 h-24 rounded-2xl overflow-hidden flex items-center justify-center text-2xl font-semibold shadow-sm border-2',
              colors.border,
              !photoBlobUrl && cn(colors.primary, colors.primaryText),
            )}
          >
            {loading ? (
              <span className={cn('text-sm font-normal', colors.textMuted)}>…</span>
            ) : photoBlobUrl ? (
              <img src={photoBlobUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              userInitials(user)
            )}
          </div>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || removing}
            className={cn(
              'absolute -bottom-2 -right-2 p-2 rounded-xl border shadow-md transition-colors',
              colors.surface,
              colors.border,
              colors.interactiveHover,
            )}
            aria-label="Change profile photo"
          >
            <Camera className="w-4 h-4" aria-hidden />
          </button>
        </div>

        <div className="flex-1 min-w-0 text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
            <UserIcon className={cn('w-4 h-4', colors.textMuted)} aria-hidden />
            <h3 className={cn('text-sm font-semibold', colors.text)}>Profile photo</h3>
          </div>
          <p className={cn('text-sm font-medium truncate', colors.text)}>{displayName}</p>
          <p className={cn('text-xs mt-1 leading-relaxed', colors.textMuted)}>
            PNG, JPG, WEBP or GIF · max 2 MB. Shown in the top bar, sidebar, and user screens.
          </p>
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-4">
            <Button
              type="button"
              variant="secondary"
              size="default"
              disabled={uploading || removing}
              loading={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {user.userPhotoPath ? 'Replace photo' : 'Upload photo'}
            </Button>
            {user.userPhotoPath && (
              <Button
                type="button"
                variant="secondary"
                size="default"
                disabled={uploading || removing}
                loading={removing}
                onClick={handleRemove}
                className="inline-flex items-center gap-1.5 text-red-600 hover:text-red-700 dark:text-red-400"
              >
                <Trash2 className="w-4 h-4" aria-hidden />
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp,.gif,image/png,image/jpeg,image/webp,image/gif"
        className="sr-only"
        onChange={handleFileChange}
      />
    </div>
  );
};

export default UserPhotoSection;
